/**
 * Background animado em Canvas — paisagem natural com parallax.
 *
 * Estrutura em camadas (do fundo pra frente):
 *   sky → sun → mountains-far → mountains-near → clouds → birds →
 *   ground → trees → flowers → grass → particles
 *
 * Tudo é desenhado num único <canvas> fixo (z-index: -1) atrás do conteúdo
 * do hub. O loop usa requestAnimationFrame com delta-time, respeita
 * prefers-reduced-motion, pausa em background-tab, e auto-reduz detalhe
 * em telas pequenas.
 *
 * API: mountBackground(parent, config?) → cleanup function.
 */

// ============================================================================
// TIPOS E CONFIGURAÇÃO
// ============================================================================

export type TimeOfDay = 'morning' | 'afternoon' | 'sunset';
export type DetailLevel = 'low' | 'medium' | 'high';

export interface BackgroundConfig {
  /** Multiplicador da velocidade das nuvens. 1.0 = padrão. */
  cloudSpeed: number;
  /** Multiplicador da velocidade do sol (muito lento de propósito). */
  sunSpeed: number;
  /** Quantidade de partículas (folhas/pólen) flutuando. */
  particleCount: number;
  /** Intensidade do brilho radial do sol. 0–1. */
  lightIntensity: number;
  /** Nível de detalhe — afeta quantidade de árvores, grama, flores. */
  detailLevel: DetailLevel;
  /** Horário do dia — muda paleta de cores e posição do sol. */
  timeOfDay: TimeOfDay;
  /** Habilita pássaros voando ocasionalmente. */
  enableBirds: boolean;
}

const DEFAULT_CONFIG: BackgroundConfig = {
  cloudSpeed: 1.0,
  sunSpeed: 1.0,
  particleCount: 18,
  lightIntensity: 0.75,
  detailLevel: 'medium',
  timeOfDay: 'morning',
  enableBirds: true,
};

// ============================================================================
// PALETAS POR HORÁRIO — gradiente do céu, cor do sol, tons das montanhas etc.
// ============================================================================

interface Palette {
  skyTop: string;
  skyMid: string;
  skyBottom: string;
  sunCore: string;
  sunGlow: string;
  mountainFar: string;
  mountainNear: string;
  groundFar: string;
  groundNear: string;
  treeTrunk: string;
  treeLeaves: string;
  treeLeavesShade: string;
  grass: string;
  grassDark: string;
  flowerColors: string[];
  cloudColor: string;
  cloudShadow: string;
  birdColor: string;
  particleColor: string;
  /** Posição relativa do sol (0 = nascente esquerda, 1 = poente direita). */
  sunBaseX: number;
  sunBaseY: number;
}

const PALETTES: Record<TimeOfDay, Palette> = {
  morning: {
    skyTop: '#7ec8f5',
    skyMid: '#b6e2f7',
    skyBottom: '#fce8c2',
    sunCore: '#fff8dc',
    sunGlow: 'rgba(255, 230, 150, 0.55)',
    mountainFar: '#9bb5cc',
    mountainNear: '#6e8aa3',
    groundFar: '#8ec07c',
    groundNear: '#5e9c4f',
    treeTrunk: '#5a3e2b',
    treeLeaves: '#3f7d3b',
    treeLeavesShade: '#2f5e2c',
    grass: '#4fae3c',
    grassDark: '#3a8a2a',
    flowerColors: ['#ffd54f', '#ff6f91', '#ffffff', '#ba68c8', '#ff7043'],
    cloudColor: 'rgba(255, 255, 255, 0.92)',
    cloudShadow: 'rgba(200, 215, 230, 0.55)',
    birdColor: '#2c3e50',
    particleColor: 'rgba(255, 245, 200, 0.7)',
    sunBaseX: 0.18,
    sunBaseY: 0.22,
  },
  afternoon: {
    skyTop: '#5fa8e8',
    skyMid: '#9dd3f5',
    skyBottom: '#dff1fb',
    sunCore: '#fffbe6',
    sunGlow: 'rgba(255, 245, 180, 0.45)',
    mountainFar: '#8aa7c0',
    mountainNear: '#5d7a93',
    groundFar: '#7eb86c',
    groundNear: '#509942',
    treeTrunk: '#4a3322',
    treeLeaves: '#357032',
    treeLeavesShade: '#264f24',
    grass: '#48a338',
    grassDark: '#357f26',
    flowerColors: ['#ffeb3b', '#e91e63', '#ffffff', '#9c27b0', '#ff5722'],
    cloudColor: 'rgba(255, 255, 255, 0.95)',
    cloudShadow: 'rgba(180, 200, 220, 0.5)',
    birdColor: '#1a252f',
    particleColor: 'rgba(255, 255, 220, 0.6)',
    sunBaseX: 0.5,
    sunBaseY: 0.12,
  },
  sunset: {
    skyTop: '#3d4f7e',
    skyMid: '#e88a5f',
    skyBottom: '#ffc785',
    sunCore: '#fff0c2',
    sunGlow: 'rgba(255, 150, 80, 0.65)',
    mountainFar: '#7a6585',
    mountainNear: '#4e3f5a',
    groundFar: '#6b8f5f',
    groundNear: '#3f6b39',
    treeTrunk: '#3a2418',
    treeLeaves: '#2f5a35',
    treeLeavesShade: '#1f3f24',
    grass: '#3f8230',
    grassDark: '#2a601e',
    flowerColors: ['#ffb74d', '#f06292', '#ffe082', '#ce93d8', '#ff8a65'],
    cloudColor: 'rgba(255, 220, 180, 0.9)',
    cloudShadow: 'rgba(190, 130, 100, 0.55)',
    birdColor: '#1e1014',
    particleColor: 'rgba(255, 200, 130, 0.75)',
    sunBaseX: 0.82,
    sunBaseY: 0.45,
  },
};

// ============================================================================
// MODELOS DOS OBJETOS DA CENA
// ============================================================================

interface Cloud {
  x: number;
  y: number;
  scale: number;
  speed: number;
  opacity: number;
  /** 0 = mais distante (lento), 1 = mais próximo (rápido). */
  layer: number;
}

interface Mountain {
  /** Lista de picos (x, altura relativa) usado para desenhar a silhueta. */
  peaks: { x: number; h: number }[];
}

interface Tree {
  x: number;
  /** Posição vertical relativa ao solo (0 = no chão, valores positivos = mais à frente). */
  yOffset: number;
  scale: number;
  swayPhase: number;
  /** Variação de cor leve. */
  hueShift: number;
}

interface FlowerPatch {
  x: number;
  yOffset: number;
  colorIndex: number;
  count: number;
}

interface GrassBlade {
  x: number;
  yOffset: number;
  height: number;
  swayPhase: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  rotSpeed: number;
}

interface Bird {
  x: number;
  y: number;
  vx: number;
  wingPhase: number;
  scale: number;
  active: boolean;
  cooldown: number;
}

interface SceneState {
  clouds: Cloud[];
  mountainsFar: Mountain;
  mountainsNear: Mountain;
  trees: Tree[];
  flowers: FlowerPatch[];
  grass: GrassBlade[];
  particles: Particle[];
  birds: Bird[];
  /** Tempo acumulado em segundos — usado para ciclos lentos (sol, ondas). */
  time: number;
}

// ============================================================================
// HELPERS DE RANDOMNESS DETERMINÍSTICA
// (mesmo seed = mesma cena em recargas, evita "flicker" entre renders)
// ============================================================================

function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

// ============================================================================
// CRIAÇÃO INICIAL DA CENA (chamada uma vez e em resize)
// ============================================================================

function createScene(w: number, h: number, cfg: BackgroundConfig): SceneState {
  const rng = makeRng(0xc0ffee);
  const detailMult = cfg.detailLevel === 'low' ? 0.5 : cfg.detailLevel === 'high' ? 1.5 : 1.0;

  // ----- Nuvens em 3 camadas com paralaxe -----
  const cloudCount = Math.round(8 * detailMult);
  const clouds: Cloud[] = [];
  for (let i = 0; i < cloudCount; i++) {
    const layer = rng();
    clouds.push({
      x: rng() * w,
      y: h * (0.05 + rng() * 0.25),
      scale: 0.6 + rng() * 0.9 + layer * 0.4,
      speed: 6 + layer * 18, // px/s — multiplicado por config.cloudSpeed no update
      opacity: 0.7 + layer * 0.3,
      layer,
    });
  }

  // ----- Montanhas (silhuetas baseadas em ruído) -----
  const mountainsFar = makeMountainSilhouette(w, h * 0.45, 12, 0.55, makeRng(0xfa12));
  const mountainsNear = makeMountainSilhouette(w, h * 0.55, 8, 0.85, makeRng(0xfa42));

  // ----- Árvores -----
  const treeCount = Math.round(14 * detailMult);
  const trees: Tree[] = [];
  for (let i = 0; i < treeCount; i++) {
    trees.push({
      x: rng() * w,
      yOffset: rng() * 60,
      scale: 0.55 + rng() * 0.75,
      swayPhase: rng() * Math.PI * 2,
      hueShift: (rng() - 0.5) * 12,
    });
  }
  // ordenar por yOffset pra desenhar mais distantes primeiro
  trees.sort((a, b) => a.yOffset - b.yOffset);

  // ----- Flores em pequenos canteiros -----
  const flowerCount = Math.round(18 * detailMult);
  const flowers: FlowerPatch[] = [];
  for (let i = 0; i < flowerCount; i++) {
    flowers.push({
      x: rng() * w,
      yOffset: rng() * 80,
      colorIndex: Math.floor(rng() * 5),
      count: 2 + Math.floor(rng() * 4),
    });
  }

  // ----- Grama -----
  const grassDensity = cfg.detailLevel === 'low' ? 80 : cfg.detailLevel === 'high' ? 220 : 140;
  const grass: GrassBlade[] = [];
  for (let i = 0; i < grassDensity; i++) {
    grass.push({
      x: rng() * w,
      yOffset: rng() * 90,
      height: 4 + rng() * 9,
      swayPhase: rng() * Math.PI * 2,
    });
  }

  // ----- Partículas (folhas/pólen) -----
  const particles: Particle[] = [];
  for (let i = 0; i < cfg.particleCount; i++) {
    particles.push(spawnParticle(w, h, rng, true));
  }

  // ----- Pássaros (começam inativos, aparecem ocasionalmente) -----
  const birds: Bird[] = [];
  if (cfg.enableBirds) {
    for (let i = 0; i < 3; i++) {
      birds.push({
        x: -100,
        y: 0,
        vx: 0,
        wingPhase: 0,
        scale: 1,
        active: false,
        cooldown: 3 + rng() * 8, // segundos até o primeiro voo
      });
    }
  }

  return {
    clouds,
    mountainsFar,
    mountainsNear,
    trees,
    flowers,
    grass,
    particles,
    birds,
    time: 0,
  };
}

function makeMountainSilhouette(w: number, peakHeight: number, peakCount: number, jitter: number, rng: () => number): Mountain {
  const peaks: { x: number; h: number }[] = [];
  const step = w / (peakCount - 1);
  for (let i = 0; i < peakCount; i++) {
    peaks.push({
      x: i * step + (rng() - 0.5) * step * 0.3,
      h: peakHeight * (0.6 + rng() * jitter),
    });
  }
  return { peaks };
}

function spawnParticle(w: number, h: number, rng: () => number, randomY: boolean): Particle {
  return {
    x: rng() * w,
    y: randomY ? rng() * h * 0.7 : -10,
    vx: -10 + rng() * 30, // arrasto suave pra direita
    vy: 8 + rng() * 14,   // cai devagar
    size: 2 + rng() * 3,
    rotation: rng() * Math.PI * 2,
    rotSpeed: (rng() - 0.5) * 2,
  };
}

// ============================================================================
// UPDATE — chamado a cada frame com delta time (segundos)
// ============================================================================

function updateScene(scene: SceneState, w: number, h: number, dt: number, cfg: BackgroundConfig): void {
  scene.time += dt;

  // Nuvens deslizam para a direita; reaparecem à esquerda quando saem da tela
  for (const c of scene.clouds) {
    c.x += c.speed * cfg.cloudSpeed * dt;
    const cloudWidth = 200 * c.scale;
    if (c.x - cloudWidth > w) {
      c.x = -cloudWidth;
      c.y = h * (0.05 + Math.random() * 0.25);
    }
  }

  // Partículas (folhas) caem e se movem horizontalmente
  for (const p of scene.particles) {
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.rotation += p.rotSpeed * dt;
    if (p.y > h * 0.85 || p.x > w + 20 || p.x < -20) {
      p.x = Math.random() * w;
      p.y = -10;
      p.vx = -10 + Math.random() * 30;
      p.vy = 8 + Math.random() * 14;
    }
  }

  // Pássaros — ativam aleatoriamente, atravessam a tela, depois cooldown
  for (const b of scene.birds) {
    if (b.active) {
      b.x += b.vx * dt;
      b.wingPhase += dt * 12;
      if ((b.vx > 0 && b.x > w + 50) || (b.vx < 0 && b.x < -50)) {
        b.active = false;
        b.cooldown = 8 + Math.random() * 20;
      }
    } else {
      b.cooldown -= dt;
      if (b.cooldown <= 0) {
        const ltr = Math.random() > 0.5;
        b.scale = 0.6 + Math.random() * 0.8;
        b.vx = (ltr ? 1 : -1) * (40 + Math.random() * 50);
        b.x = ltr ? -50 : w + 50;
        b.y = h * (0.08 + Math.random() * 0.25);
        b.active = true;
      }
    }
  }
}

// ============================================================================
// RENDER — funções de desenho, uma por camada
// ============================================================================

function drawSky(ctx: CanvasRenderingContext2D, w: number, h: number, palette: Palette): void {
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, palette.skyTop);
  grad.addColorStop(0.6, palette.skyMid);
  grad.addColorStop(1, palette.skyBottom);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
}

function drawSun(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  palette: Palette,
  time: number,
  cfg: BackgroundConfig,
): void {
  // O sol percorre um arco lento ao longo do tempo (sunSpeed controla a velocidade).
  // Em 5 minutos reais cruza ~20% do céu — muito sutil mas perceptível.
  const drift = Math.sin(time * 0.01 * cfg.sunSpeed) * 0.08;
  const sunX = (palette.sunBaseX + drift) * w;
  const sunY = (palette.sunBaseY + Math.abs(drift) * 0.3) * h;
  const radius = Math.min(w, h) * 0.08;

  // Halo externo amplo
  const haloR = radius * 6;
  const haloGrad = ctx.createRadialGradient(sunX, sunY, radius * 0.8, sunX, sunY, haloR);
  haloGrad.addColorStop(0, palette.sunGlow);
  haloGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.globalAlpha = cfg.lightIntensity;
  ctx.fillStyle = haloGrad;
  ctx.fillRect(sunX - haloR, sunY - haloR, haloR * 2, haloR * 2);
  ctx.globalAlpha = 1;

  // Disco do sol com gradiente interno
  const coreGrad = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, radius);
  coreGrad.addColorStop(0, '#ffffff');
  coreGrad.addColorStop(0.4, palette.sunCore);
  coreGrad.addColorStop(1, palette.sunGlow);
  ctx.fillStyle = coreGrad;
  ctx.beginPath();
  ctx.arc(sunX, sunY, radius, 0, Math.PI * 2);
  ctx.fill();
}

function drawMountain(
  ctx: CanvasRenderingContext2D,
  w: number,
  baseY: number,
  m: Mountain,
  color: string,
  shadowColor: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, baseY);
  for (const peak of m.peaks) {
    ctx.lineTo(peak.x, baseY - peak.h);
  }
  ctx.lineTo(w, baseY);
  ctx.closePath();
  ctx.fill();

  // Sombra lateral pra dar volume
  ctx.fillStyle = shadowColor;
  ctx.globalAlpha = 0.25;
  ctx.beginPath();
  ctx.moveTo(0, baseY);
  for (const peak of m.peaks) {
    ctx.lineTo(peak.x + 10, baseY - peak.h * 0.9);
    ctx.lineTo(peak.x, baseY - peak.h);
  }
  ctx.lineTo(w, baseY);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;
}

/**
 * Cada puff é desenhado em 3 camadas pra simular volume:
 *   1. Sombra inferior (mais escura) deslocada pra baixo
 *   2. Corpo principal
 *   3. Highlight superior (mais claro) deslocado pra cima
 *
 * Como Canvas 2D não tem soft shadows performáticos, usamos múltiplos
 * círculos com gradients radiais sobrepostos. O resultado lembra uma nuvem
 * volumétrica em vez de blobs lisos.
 */
function drawCloud(ctx: CanvasRenderingContext2D, c: Cloud, palette: Palette): void {
  ctx.save();
  ctx.translate(c.x, c.y);
  ctx.scale(c.scale, c.scale);
  ctx.globalAlpha = c.opacity;

  // Sombra suave da nuvem inteira no chão imaginário (sombra projetada)
  const shadowGrad = ctx.createRadialGradient(0, 18, 5, 0, 18, 90);
  shadowGrad.addColorStop(0, palette.cloudShadow);
  shadowGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = shadowGrad;
  ctx.fillRect(-95, 5, 190, 30);

  // Puffs com tamanhos variados pra silhueta orgânica
  const puffs = [
    { x: 0, y: 2, r: 30 },
    { x: 28, y: -4, r: 26 },
    { x: -30, y: -2, r: 28 },
    { x: 55, y: 6, r: 20 },
    { x: -56, y: 4, r: 22 },
    { x: 12, y: -18, r: 22 },
    { x: -16, y: -22, r: 18 },
    { x: 40, y: -10, r: 16 },
  ];

  // Camada 1: sombra inferior de cada puff (mais escura, deslocada pra baixo)
  for (const p of puffs) {
    const g = ctx.createRadialGradient(p.x, p.y + p.r * 0.3, p.r * 0.1, p.x, p.y + p.r * 0.3, p.r * 1.2);
    g.addColorStop(0, palette.cloudShadow);
    g.addColorStop(0.7, palette.cloudShadow.replace(/[\d.]+\)$/, '0.25)'));
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(p.x, p.y + p.r * 0.3, p.r * 1.15, 0, Math.PI * 2);
    ctx.fill();
  }

  // Camada 2: corpo principal (gradient do centro pro borda pra borda suave)
  for (const p of puffs) {
    const g = ctx.createRadialGradient(p.x - p.r * 0.2, p.y - p.r * 0.3, p.r * 0.1, p.x, p.y, p.r);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(0.55, palette.cloudColor);
    g.addColorStop(0.95, palette.cloudColor.replace(/[\d.]+\)$/, '0.7)'));
    g.addColorStop(1, palette.cloudColor.replace(/[\d.]+\)$/, '0)'));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Camada 3: highlight superior (toque mais claro no topo de cada puff)
  for (const p of puffs) {
    const hx = p.x - p.r * 0.25;
    const hy = p.y - p.r * 0.45;
    const hr = p.r * 0.55;
    const g = ctx.createRadialGradient(hx, hy, 0, hx, hy, hr);
    g.addColorStop(0, 'rgba(255,255,255,0.85)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(hx, hy, hr, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawGround(ctx: CanvasRenderingContext2D, w: number, h: number, palette: Palette): void {
  const groundY = h * 0.7;
  const grad = ctx.createLinearGradient(0, groundY, 0, h);
  grad.addColorStop(0, palette.groundFar);
  grad.addColorStop(1, palette.groundNear);
  ctx.fillStyle = grad;
  ctx.fillRect(0, groundY, w, h - groundY);
}

/**
 * Árvore semi-realista:
 *   - Tronco com taper (afina do chão pro topo) usando trapézio + gradient
 *     vertical pra dar textura (mais claro de um lado).
 *   - Dois galhos curtos saindo do meio do tronco.
 *   - Copa formada por 5–6 "bolhas" de folhagem sobrepostas com cores
 *     ligeiramente diferentes — silhueta orgânica em vez de círculo perfeito.
 *   - Sombra de fundo deslocada e highlights pontuais pra dar volume.
 *
 * O sway aplica-se à copa inteira e aos galhos, mas não ao tronco —
 * dá sensação de vento mexendo só nas folhas.
 */
function drawTree(
  ctx: CanvasRenderingContext2D,
  t: Tree,
  groundY: number,
  palette: Palette,
  time: number,
): void {
  const baseY = groundY + t.yOffset;
  const trunkH = 38 * t.scale;
  const trunkBaseW = 8 * t.scale;
  const trunkTopW = 5 * t.scale;
  const crownR = 26 * t.scale;
  const sway = Math.sin(time * 0.8 + t.swayPhase) * 2.2 * t.scale;

  ctx.save();
  ctx.translate(t.x, baseY);

  // ---------- TRONCO (trapézio com taper + gradiente lateral) ----------
  const trunkGrad = ctx.createLinearGradient(-trunkBaseW / 2, 0, trunkBaseW / 2, 0);
  trunkGrad.addColorStop(0, shadeColor(palette.treeTrunk, -25));
  trunkGrad.addColorStop(0.5, palette.treeTrunk);
  trunkGrad.addColorStop(1, shadeColor(palette.treeTrunk, -10));
  ctx.fillStyle = trunkGrad;
  ctx.beginPath();
  ctx.moveTo(-trunkBaseW / 2, 0);
  ctx.lineTo(trunkBaseW / 2, 0);
  ctx.lineTo(trunkTopW / 2, -trunkH);
  ctx.lineTo(-trunkTopW / 2, -trunkH);
  ctx.closePath();
  ctx.fill();

  // Textura sutil: duas linhas verticais escuras pra simular ranhuras
  ctx.strokeStyle = shadeColor(palette.treeTrunk, -30);
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.moveTo(-trunkBaseW * 0.15, -trunkH * 0.15);
  ctx.lineTo(-trunkBaseW * 0.08, -trunkH * 0.85);
  ctx.moveTo(trunkBaseW * 0.18, -trunkH * 0.25);
  ctx.lineTo(trunkBaseW * 0.1, -trunkH * 0.75);
  ctx.stroke();

  // ---------- GALHOS (saindo dos dois lados do meio do tronco) ----------
  ctx.strokeStyle = shadeColor(palette.treeTrunk, -15);
  ctx.lineWidth = 1.8 * t.scale;
  ctx.lineCap = 'round';
  ctx.beginPath();
  // Galho esquerdo
  ctx.moveTo(-trunkBaseW * 0.3, -trunkH * 0.55);
  ctx.quadraticCurveTo(-trunkH * 0.35 + sway * 0.4, -trunkH * 0.7, -trunkH * 0.45 + sway * 0.6, -trunkH * 0.85);
  // Galho direito
  ctx.moveTo(trunkBaseW * 0.3, -trunkH * 0.65);
  ctx.quadraticCurveTo(trunkH * 0.3 + sway * 0.4, -trunkH * 0.8, trunkH * 0.4 + sway * 0.6, -trunkH * 0.95);
  ctx.stroke();

  // ---------- COPA — 6 bolhas sobrepostas formando silhueta orgânica ----------
  const crownCenterY = -trunkH - crownR * 0.3;
  const blobs = [
    { x: 0, y: 0, r: crownR * 1.0, shade: 0 },
    { x: -crownR * 0.55, y: crownR * 0.15, r: crownR * 0.75, shade: -12 },
    { x: crownR * 0.55, y: crownR * 0.1, r: crownR * 0.8, shade: -6 },
    { x: -crownR * 0.25, y: -crownR * 0.5, r: crownR * 0.7, shade: 8 },
    { x: crownR * 0.3, y: -crownR * 0.55, r: crownR * 0.72, shade: 12 },
    { x: 0, y: crownR * 0.4, r: crownR * 0.65, shade: -20 }, // base mais escura
  ];

  // Sombra geral da copa atrás de tudo
  ctx.fillStyle = palette.treeLeavesShade;
  for (const b of blobs) {
    ctx.beginPath();
    ctx.arc(sway + b.x + 3, crownCenterY + b.y + 3, b.r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Folhagem principal com sombreamento por bolha
  for (const b of blobs) {
    const baseColor = b.shade < 0
      ? shadeColor(palette.treeLeavesShade, b.shade + 15)
      : shadeColor(palette.treeLeaves, b.shade);
    const g = ctx.createRadialGradient(
      sway + b.x - b.r * 0.3, crownCenterY + b.y - b.r * 0.4, b.r * 0.1,
      sway + b.x, crownCenterY + b.y, b.r,
    );
    g.addColorStop(0, shadeColor(baseColor, 15 + t.hueShift));
    g.addColorStop(0.7, baseColor);
    g.addColorStop(1, shadeColor(baseColor, -20));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(sway + b.x, crownCenterY + b.y, b.r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Pontinhos claros simulando folhas iluminadas no topo
  // (tamanho deterministico via hueShift pra não piscarem entre frames)
  ctx.fillStyle = 'rgba(255, 255, 220, 0.35)';
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI - Math.PI * 0.7;
    const dx = Math.cos(a) * crownR * 0.7;
    const dy = Math.sin(a) * crownR * 0.6;
    const r = 2 + ((i + Math.abs(t.hueShift)) % 2) * 0.7;
    ctx.beginPath();
    ctx.arc(sway + dx, crownCenterY + dy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

/**
 * Escurece ou clareia uma cor hex/rgb por uma porcentagem (-100 a +100).
 * Aceita "#rrggbb", "#rgb" ou "rgba(...)". Retorna no formato original
 * quando possível.
 */
function shadeColor(color: string, percent: number): string {
  // Trata rgba(...)
  const rgbaMatch = color.match(/rgba?\(([^)]+)\)/);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(',').map((s) => s.trim());
    const r = clampShade(parseInt(parts[0], 10), percent);
    const g = clampShade(parseInt(parts[1], 10), percent);
    const b = clampShade(parseInt(parts[2], 10), percent);
    const a = parts[3] ?? '1';
    return `rgba(${r},${g},${b},${a})`;
  }
  // Trata hex
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  const r = clampShade(parseInt(hex.substring(0, 2), 16), percent);
  const g = clampShade(parseInt(hex.substring(2, 4), 16), percent);
  const b = clampShade(parseInt(hex.substring(4, 6), 16), percent);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function clampShade(v: number, percent: number): number {
  return Math.max(0, Math.min(255, Math.round(v + (v * percent) / 100)));
}

function toHex(v: number): string {
  return v.toString(16).padStart(2, '0');
}

function drawFlower(
  ctx: CanvasRenderingContext2D,
  f: FlowerPatch,
  groundY: number,
  palette: Palette,
): void {
  const baseY = groundY + f.yOffset;
  const color = palette.flowerColors[f.colorIndex % palette.flowerColors.length];
  for (let i = 0; i < f.count; i++) {
    const ox = (i - f.count / 2) * 7;
    const oy = (i % 2) * 3;
    // Caule
    ctx.strokeStyle = palette.grassDark;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(f.x + ox, baseY);
    ctx.lineTo(f.x + ox, baseY - 7 - oy);
    ctx.stroke();
    // Pétalas
    ctx.fillStyle = color;
    const cy = baseY - 8 - oy;
    for (let p = 0; p < 5; p++) {
      const a = (p / 5) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(f.x + ox + Math.cos(a) * 2, cy + Math.sin(a) * 2, 2, 0, Math.PI * 2);
      ctx.fill();
    }
    // Miolo
    ctx.fillStyle = '#fdd835';
    ctx.beginPath();
    ctx.arc(f.x + ox, cy, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawGrass(
  ctx: CanvasRenderingContext2D,
  grass: GrassBlade[],
  groundY: number,
  palette: Palette,
  time: number,
): void {
  ctx.strokeStyle = palette.grass;
  ctx.lineWidth = 1.5;
  for (const g of grass) {
    const baseY = groundY + g.yOffset;
    const sway = Math.sin(time * 1.3 + g.swayPhase) * 1.6;
    ctx.beginPath();
    ctx.moveTo(g.x, baseY);
    ctx.quadraticCurveTo(g.x + sway, baseY - g.height * 0.5, g.x + sway * 1.5, baseY - g.height);
    ctx.stroke();
  }
}

function drawBirds(ctx: CanvasRenderingContext2D, birds: Bird[], palette: Palette): void {
  ctx.strokeStyle = palette.birdColor;
  ctx.lineWidth = 2;
  for (const b of birds) {
    if (!b.active) continue;
    // Asas em ~ ou ^ alternando conforme wingPhase
    const wingY = Math.sin(b.wingPhase) * 4;
    const s = b.scale;
    ctx.beginPath();
    ctx.moveTo(b.x - 8 * s, b.y);
    ctx.quadraticCurveTo(b.x - 4 * s, b.y - wingY, b.x, b.y);
    ctx.quadraticCurveTo(b.x + 4 * s, b.y - wingY, b.x + 8 * s, b.y);
    ctx.stroke();
  }
}

function drawParticles(ctx: CanvasRenderingContext2D, particles: Particle[], palette: Palette): void {
  ctx.fillStyle = palette.particleColor;
  for (const p of particles) {
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rotation);
    ctx.beginPath();
    ctx.ellipse(0, 0, p.size, p.size * 0.6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

// ============================================================================
// RENDER LOOP — orquestra todas as camadas
// ============================================================================

function renderFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  scene: SceneState,
  palette: Palette,
  cfg: BackgroundConfig,
): void {
  drawSky(ctx, w, h, palette);
  drawSun(ctx, w, h, palette, scene.time, cfg);
  drawMountain(ctx, w, h * 0.7, scene.mountainsFar, palette.mountainFar, '#000');
  drawMountain(ctx, w, h * 0.72, scene.mountainsNear, palette.mountainNear, '#000');
  for (const c of scene.clouds) drawCloud(ctx, c, palette);
  drawBirds(ctx, scene.birds, palette);
  drawGround(ctx, w, h, palette);
  const groundY = h * 0.7;
  for (const t of scene.trees) drawTree(ctx, t, groundY, palette, scene.time);
  for (const f of scene.flowers) drawFlower(ctx, f, groundY, palette);
  drawGrass(ctx, scene.grass, groundY, palette, scene.time);
  drawParticles(ctx, scene.particles, palette);
}

// ============================================================================
// MOUNT / UNMOUNT — API pública
// ============================================================================

/**
 * Cria o canvas, inicia o loop de animação e retorna uma função de cleanup.
 *
 * @param parent  Elemento onde o canvas será inserido (será fixed: z-index -1).
 * @param partial Sobreescreve o DEFAULT_CONFIG por chave.
 * @returns       Função de cleanup que cancela RAF e remove o canvas.
 */
export function mountBackground(parent: HTMLElement, partial: Partial<BackgroundConfig> = {}): () => void {
  const cfg: BackgroundConfig = { ...DEFAULT_CONFIG, ...partial };

  // Auto-reduz detalhe em telas pequenas / hardware fraco
  if (window.innerWidth < 768 || (navigator.hardwareConcurrency ?? 8) <= 4) {
    if (cfg.detailLevel === 'high') cfg.detailLevel = 'medium';
    else if (cfg.detailLevel === 'medium') cfg.detailLevel = 'low';
    cfg.particleCount = Math.round(cfg.particleCount * 0.6);
  }

  // Respeita prefers-reduced-motion — animações ficam estáticas
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const canvas = document.createElement('canvas');
  canvas.className = 'bg-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  parent.appendChild(canvas);

  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    canvas.remove();
    return () => {};
  }

  let scene: SceneState = createScene(window.innerWidth, window.innerHeight, cfg);
  let cssW = window.innerWidth;
  let cssH = window.innerHeight;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  function resize() {
    cssW = window.innerWidth;
    cssH = window.innerHeight;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = cssW + 'px';
    canvas.style.height = cssH + 'px';
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    scene = createScene(cssW, cssH, cfg);
  }
  resize();

  let lastT = performance.now();
  let rafId = 0;
  let stopped = false;

  // Primeiro frame imediato — garante cena visível mesmo se a aba começar oculta
  // (o navegador suspende RAF em background tabs).
  renderFrame(ctx!, cssW, cssH, scene, PALETTES[cfg.timeOfDay], cfg);

  function loop(now: number) {
    if (stopped) return;
    if (!document.body.contains(canvas)) {
      // canvas foi removido por outro caminho — auto-cleanup
      stopped = true;
      return;
    }
    const dt = Math.min(0.1, (now - lastT) / 1000); // clamp pra evitar saltos quando aba estava oculta
    lastT = now;

    // Só atualiza física quando visível (economiza CPU em background tab).
    // Render sempre roda — barato e mantém a cena consistente após resize/etc.
    if (!document.hidden && !reduceMotion) {
      updateScene(scene, cssW, cssH, dt, cfg);
    }
    renderFrame(ctx!, cssW, cssH, scene, PALETTES[cfg.timeOfDay], cfg);
    rafId = requestAnimationFrame(loop);
  }

  rafId = requestAnimationFrame(loop);

  const onResize = () => resize();
  window.addEventListener('resize', onResize, { passive: true });

  return () => {
    stopped = true;
    cancelAnimationFrame(rafId);
    window.removeEventListener('resize', onResize);
    canvas.remove();
  };
}
